import { useEffect, useState } from 'react';

/**
 * Tracks the height of the *visual* viewport — the area actually visible to the
 * user, which shrinks when the on-screen keyboard opens on mobile (unlike
 * `100vh`/`window.innerHeight`, which stay at the full layout-viewport height).
 *
 * Returns the height in pixels, or `undefined` when the VisualViewport API is
 * unavailable so callers can fall back to a CSS height (e.g. `'100%'`).
 */
export const useVisualViewportHeight = (): number | undefined => {
  const [height, setHeight] = useState<number | undefined>(() =>
    typeof window !== 'undefined' ? window.visualViewport?.height : undefined,
  );

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => setHeight(viewport.height);
    update();

    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return height;
};
